import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as ProcessIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import MaturedDealSlipModal from '../components/MaturedDealSlipModal';

function toYmd(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const MaturityHandlingPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [maturities, setMaturities] = useState([]);
  const [totals, setTotals] = useState({ banks: {}, net_cashflow: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [maturityAction, setMaturityAction] = useState('');
  const [bankPaymentCode, setBankPaymentCode] = useState('');
  const [settlementModes, setSettlementModes] = useState([]);
  const [dealSlip, setDealSlip] = useState({ open: false, transaction: null, dealType: 'GSec' });
  const [maturedSlip, setMaturedSlip] = useState({
    open: false,
    transaction: null,
    dealType: 'GSec',
    variant: 'matured'
  });
  const [slipChoice, setSlipChoice] = useState({ open: false, deal: null });
  const [slipLoading, setSlipLoading] = useState(false);

  // Period selection
  const [viewMode2, setViewMode2] = useState('single'); // 'single' | 'period'
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [periodRows, setPeriodRows] = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);

  const [summary, setSummary] = useState({
    totalDeals: 0,
    totalMaturityAmount: 0,
    addCount: 0,
    lessCount: 0,
    netCashflow: 0
  });
  const [viewMode, setViewMode] = useState('current');
  const [systemDate, setSystemDate] = useState('');

  const dealTypes = ['all', 'money_market', 'gsec', 'repo', 'buyback'];
  const statusTypes = ['all', 'pending', 'processed', 'failed', 'Approved', 'final_approved', 'Active'];

  useEffect(() => {
    fetchMaturities();
    fetchSettlementModes();
  }, [selectedDate, filterType, filterStatus]);

  const fetchMaturities = async () => {
    setLoading(true);
    setError('');
    try {
      const dateStr = toYmd(selectedDate);
      const params = new URLSearchParams({
        date: dateStr,
        type: filterType !== 'all' ? filterType : '',
        status: filterStatus !== 'all' ? filterStatus : ''
      });

      const response = await axios.get(`${API_BASE_URL}/maturity/handling?${params}`);

      if (response.data.success) {
        const data = response.data.data || [];
        setMaturities(data);
        setTotals(response.data.totals || { banks: {}, net_cashflow: 0 });
        setViewMode(response.data.view_mode || 'current');
        setSystemDate(response.data.system_date || '');
        setSelectedDeals([]);
        calculateSummary(data, response.data.totals);
      } else {
        setError(response.data.message || 'Failed to fetch maturity data');
      }
    } catch (err) {
      setError('Error fetching maturity data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlementModes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payment-master/modes`);
      if (response.data && response.data.data) {
        setSettlementModes(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching settlement modes:', err);
    }
  };

  const calculateSummary = (data, apiTotals) => {
    const addCount = data.filter((d) => d.cash_flow === 'Add').length;
    const lessCount = data.filter((d) => d.cash_flow === 'Less').length;
    const totalMaturityAmount = data.reduce(
      (sum, deal) => sum + Math.abs(parseFloat(deal.maturity_amount || 0)),
      0
    );

    setSummary({
      totalDeals: data.length,
      totalMaturityAmount,
      addCount,
      lessCount,
      netCashflow: apiTotals?.net_cashflow ?? 0
    });
  };

  const rowKey = (deal) => `${deal.deal_type}-${deal.event_type || 'maturity'}-${deal.id}`;

  const handleDealSelect = (deal) => {
    const key = rowKey(deal);
    setSelectedDeals((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    const selectableDeals = maturities.filter((deal) => deal.is_selectable);
    if (selectedDeals.length === selectableDeals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(selectableDeals.map((deal) => rowKey(deal)));
    }
  };

  const getSelectedMaturityRows = () =>
    maturities.filter(
      (d) => selectedDeals.includes(rowKey(d)) && d.is_selectable && d.event_type !== 'settlement'
    );

  const handleProcessMaturities = async () => {
    const selectedItems = getSelectedMaturityRows();
    if (selectedItems.length === 0) {
      setError('Please select maturity deals to process');
      return;
    }

    if (selectedItems.some((d) => d.deal_type === 'buyback')) {
      setError('Buyback leg2 settlements are posted via EOD. Select MM, GSEC, or Repo deals for maturity processing.');
      return;
    }

    if (maturityAction === 'principal_reinvest_interest_paid') {
      if (selectedItems.length > 1) {
        setError('Please select only one deal for principal reinvestment');
        return;
      }
      const selectedDeal = selectedItems[0];
      if (selectedDeal) {
        const dealData = {
          principalAmount: selectedDeal.face_value || selectedDeal.principal_amount,
          originalDealId: selectedDeal.id,
          originalDealNumber: selectedDeal.deal_number,
          maturityMethod: 'principal_reinvestment_interest_payment'
        };
        localStorage.setItem('reinvestmentDealData', JSON.stringify(dealData));
        if (selectedDeal.deal_type === 'gsec') window.location.href = '/fixed-income-gsec';
        else if (selectedDeal.deal_type === 'money_market') window.location.href = '/money-market-deals';
        else if (selectedDeal.deal_type === 'repo') window.location.href = '/repo-deals';
        setShowProcessDialog(false);
        return;
      }
    }

    if (maturityAction === 'principal_interest_reinvest') {
      if (selectedItems.length > 1) {
        setError('Please select only one deal for principal and interest reinvestment');
        return;
      }
      const selectedDeal = selectedItems[0];
      if (selectedDeal) {
        const dealData = {
          principalAmount:
            selectedDeal.maturity_amount || selectedDeal.face_value || selectedDeal.principal_amount,
          originalDealId: selectedDeal.id,
          originalDealNumber: selectedDeal.deal_number,
          maturityMethod: 'principal_interest_reinvestment'
        };
        localStorage.setItem('reinvestmentDealData', JSON.stringify(dealData));
        if (selectedDeal.deal_type === 'gsec') window.location.href = '/fixed-income-gsec';
        else if (selectedDeal.deal_type === 'money_market') window.location.href = '/money-market-deals';
        else if (selectedDeal.deal_type === 'repo') window.location.href = '/repo-deals';
        setShowProcessDialog(false);
        return;
      }
    }

    if (
      (maturityAction === 'principal_interest_full_payment' ||
        maturityAction === 'principal_reinvest_interest_paid') &&
      !bankPaymentCode
    ) {
      setError('Please select a settlement mode for processing');
      return;
    }

    if (!maturityAction) {
      setError('Please select a maturity action');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.post(
        `${API_BASE_URL}/maturity/process`,
        {
          dealIds: selectedItems.map((d) => d.id),
          processDate: toYmd(selectedDate),
          bankPaymentCode,
          maturityAction
        },
        {
          headers: { 'x-user-data': JSON.stringify(user) }
        }
      );

      if (response.data.success) {
        setSuccess(`Successfully processed ${selectedItems.length} deals`);
        setSelectedDeals([]);
        setShowProcessDialog(false);
        setMaturityAction('');
        setBankPaymentCode('');
        fetchMaturities();
      } else if (response.data.requiresAuthorization) {
        setError(
          `Authorization required: ${response.data.error}. Required level: ${response.data.authorizationLevel}`
        );
      } else {
        setError(response.data.message || 'Failed to process deals');
      }
    } catch (err) {
      setError('Error processing deals: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleExportMaturities = async () => {
    try {
      const dateStr = toYmd(selectedDate);
      const params = new URLSearchParams({
        date: dateStr,
        type: filterType !== 'all' ? filterType : '',
        status: filterStatus !== 'all' ? filterStatus : ''
      });

      const response = await axios.get(`${API_BASE_URL}/maturity/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `daily-maturity-cashflow-${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error exporting data: ' + err.message);
    }
  };

  const fetchPeriodMaturities = async () => {
    if (!periodFrom || !periodTo) {
      setError('Please select both From and To dates for period view');
      return;
    }
    setPeriodLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ dateFrom: periodFrom, dateTo: periodTo });
      if (filterType !== 'all') params.append('type', filterType);
      const response = await axios.get(`${API_BASE_URL}/maturity/handling-period?${params}`);
      if (response.data.success) {
        setPeriodRows(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to fetch period maturities');
      }
    } catch (err) {
      setError('Error fetching period maturities: ' + err.message);
    } finally {
      setPeriodLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (!isFinite(n) || n === 0) return '';
    const abs = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(n));
    return n < 0 ? `(${abs})` : abs;
  };

  const settlementValue = (deal) =>
    deal.settlement_value != null
      ? Number(deal.settlement_value)
      : deal.cash_flow === 'Less'
        ? -Math.abs(Number(deal.maturity_amount) || 0)
        : Math.abs(Number(deal.maturity_amount) || 0);

  // Clicking a settlement value asks which slip to open
  // (deal slip / matured deal slip / roll-over deal slip).
  // New Deal (value-date settlement) lines only have a deal slip.
  const handleSettlementClick = (deal) => {
    if (!deal?.id || !deal?.deal_type) return;
    if (deal.event_type === 'settlement') {
      openSlip(deal, 'deal');
      return;
    }
    setSlipChoice({ open: true, deal });
  };

  // Fetch the deal ticket data, then open the chosen slip view.
  const openSlip = async (deal, variant) => {
    setSlipChoice({ open: false, deal: null });
    if (!deal?.id || !deal?.deal_type) return;
    setSlipLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/maturity/deal-ticket/${deal.deal_type}/${deal.id}`
      );
      if (response.data?.success && response.data.transaction) {
        // Carry the blotter row's instrument label so the matured slip can pick
        // the right layout (Buy-Sell / Sell-Buy / Reverse Repo).
        const transaction = { ...response.data.transaction, instrument: deal.instrument };
        const dealType = response.data.dealType || 'GSec';
        if (variant === 'matured' || variant === 'rollover') {
          setMaturedSlip({ open: true, transaction, dealType, variant });
        } else {
          setDealSlip({ open: true, transaction, dealType });
        }
      } else {
        setError(response.data?.error || 'Failed to load deal ticket');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load deal ticket');
    } finally {
      setSlipLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Daily Maturity Cashflow
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Shows deals maturing on the selected date. Buyback maturities use leg2 value date.
        </Typography>
        {viewMode === 'past' && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
            Historical maturities for {toYmd(selectedDate)}
            {systemDate ? ` (system date ${systemDate})` : ''}.
          </Alert>
        )}
        {viewMode === 'current' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />} variant="outlined">
            Live day{systemDate ? ` (${systemDate})` : ''}: maturities only.
          </Alert>
        )}
        {viewMode === 'future' && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />} variant="outlined">
            Forecast day: maturities only.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Lines
                </Typography>
                <Typography variant="h5">{summary.totalDeals}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Gross Amount
                </Typography>
                <Typography variant="h5">{formatCurrency(summary.totalMaturityAmount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Add / Less
                </Typography>
                <Typography variant="h5">
                  {summary.addCount} / {summary.lessCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Net Cashflow
                </Typography>
                <Typography
                  variant="h5"
                  color={summary.netCashflow >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(summary.netCashflow)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item>
              <Button
                variant={viewMode2 === 'single' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode2('single')}
              >
                Single Day
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={viewMode2 === 'period' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode2('period')}
              >
                Period
              </Button>
            </Grid>
          </Grid>

          {viewMode2 === 'period' ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="From Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={periodFrom}
                  onChange={(e) => setPeriodFrom(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="To Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Instrument</InputLabel>
                  <Select value={filterType} label="Instrument" onChange={(e) => setFilterType(e.target.value)}>
                    {dealTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === 'all' ? 'All Products' : type.replace('_', ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPeriodMaturities} disabled={periodLoading}>
                  {periodLoading ? 'Loading...' : 'View Period'}
                </Button>
              </Grid>
            </Grid>
          ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Sales Date"
                value={selectedDate}
                onChange={setSelectedDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Instrument</InputLabel>
                <Select
                  value={filterType}
                  label="Instrument"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {dealTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === 'all' ? 'All Products' : type.replace('_', ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  {statusTypes.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status === 'all' ? 'All Status' : status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchMaturities}
                  disabled={loading}
                >
                  View
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportMaturities}>
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ProcessIcon />}
                  onClick={() => setShowProcessDialog(true)}
                  disabled={selectedDeals.length === 0}
                >
                  Process Selected ({selectedDeals.length})
                </Button>
              </Box>
            </Grid>
          </Grid>
          )}
        </Paper>

        {viewMode2 === 'period' && periodRows.length > 0 && (
          <Paper sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Maturity Date</TableCell>
                    <TableCell>Instrument</TableCell>
                    <TableCell>Deal Number</TableCell>
                    <TableCell>Counterparty</TableCell>
                    <TableCell align="right">Face Value</TableCell>
                    <TableCell align="right">Maturity Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodRows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{formatDate(row.maturity_date)}</TableCell>
                      <TableCell>{row.deal_type ? row.deal_type.replace('_', ' ').toUpperCase() : ''}</TableCell>
                      <TableCell>{row.deal_number}</TableCell>
                      <TableCell>{row.counterparty}</TableCell>
                      <TableCell align="right">{formatCurrency(row.face_value)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.maturity_amount)}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Button
                      size="small"
                      onClick={handleSelectAll}
                      disabled={maturities.filter((d) => d.is_selectable).length === 0}
                    >
                      {selectedDeals.length === maturities.filter((d) => d.is_selectable).length &&
                      maturities.filter((d) => d.is_selectable).length > 0
                        ? 'Deselect'
                        : 'Select'}
                    </Button>
                  </TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Cash Flow</TableCell>
                  <TableCell>Instrument</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Ref. No.</TableCell>
                  <TableCell>Reference Deal</TableCell>
                  <TableCell align="right">Settlement Value</TableCell>
                  <TableCell>Val/Mat</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Val Date</TableCell>
                  <TableCell align="right">Opening Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : maturities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No cashflows for {toYmd(selectedDate)}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {maturities.map((deal) => (
                      <TableRow key={`${deal.deal_type}-${deal.event_type || 'maturity'}-${deal.id}`} hover>
                        <TableCell padding="checkbox">
                          {deal.is_selectable ? (
                            <input
                              type="checkbox"
                              checked={selectedDeals.includes(rowKey(deal))}
                              onChange={() => handleDealSelect(deal)}
                            />
                          ) : (
                            <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={deal.event_type === 'settlement' ? 'New Deal' : 'Maturity'}
                            size="small"
                            color={deal.event_type === 'settlement' ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={deal.cash_flow}
                            size="small"
                            color={deal.cash_flow === 'Add' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{deal.instrument}</TableCell>
                        <TableCell>{deal.description}</TableCell>
                        <TableCell>{deal.deal_number}</TableCell>
                        <TableCell>
                          {deal.reference_deal_number ? (
                            <Tooltip title="Originating deal">
                              <span>{deal.reference_deal_number}</span>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View deal ticket">
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleSettlementClick(deal)}
                              disabled={slipLoading}
                              sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                minWidth: 0,
                                color: 'primary.main',
                                textDecoration: 'underline'
                              }}
                            >
                              {formatCurrency(settlementValue(deal))}
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{deal.val_mat}</TableCell>
                        <TableCell>
                          <Chip label={deal.status} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDate(deal.value_date || deal.maturity_date)}</TableCell>
                        <TableCell align="right">
                          {deal.opening_balance != null ? formatCurrency(deal.opening_balance) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableCell colSpan={6} align="right">
                        <strong>Balance</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(totals.net_cashflow)}</strong>
                      </TableCell>
                      <TableCell colSpan={4} />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={showProcessDialog} onClose={() => setShowProcessDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Process Maturity Deals</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Process {selectedDeals.length} selected deal(s). Buyback lines are settled via EOD and cannot be
              processed here.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Maturity Action</InputLabel>
              <Select
                value={maturityAction}
                label="Maturity Action"
                onChange={(e) => setMaturityAction(e.target.value)}
              >
                <MenuItem value="principal_interest_full_payment">
                  1. Maturing principal and interest paid or received in full
                </MenuItem>
                <MenuItem value="principal_reinvest_interest_paid">
                  2. Maturing principal will be reinvested and interest will be paid or received
                </MenuItem>
                <MenuItem value="principal_interest_reinvest">
                  3. Maturing principal and interest will both be reinvested with new terms
                </MenuItem>
                <MenuItem value="different_amount_reinvest">4. Re-investment of a different amount</MenuItem>
              </Select>
            </FormControl>

            {(maturityAction === 'principal_interest_full_payment' ||
              maturityAction === 'principal_reinvest_interest_paid') && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Settlement Mode</InputLabel>
                <Select
                  value={bankPaymentCode}
                  label="Settlement Mode"
                  onChange={(e) => setBankPaymentCode(e.target.value)}
                >
                  {settlementModes.map((mode, idx) => (
                    <MenuItem
                      key={`${mode.payment_method}-${mode.bank_payment_code}-${idx}`}
                      value={mode.bank_payment_code}
                    >
                      {mode.payment_method} - {mode.bank_payment_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowProcessDialog(false)}>Cancel</Button>
            <Button
              onClick={handleProcessMaturities}
              variant="contained"
              disabled={
                !maturityAction ||
                processing ||
                ((maturityAction === 'principal_interest_full_payment' ||
                  maturityAction === 'principal_reinvest_interest_paid') &&
                  !bankPaymentCode)
              }
            >
              {processing ? 'Processing...' : 'Process Deals'}
            </Button>
          </DialogActions>
        </Dialog>

        {dealSlip.open && dealSlip.transaction && (
          <DealSlipViewModal
            transaction={dealSlip.transaction}
            dealType={dealSlip.dealType}
            onClose={() => setDealSlip({ open: false, transaction: null, dealType: 'GSec' })}
          />
        )}

        {maturedSlip.open && maturedSlip.transaction && (
          <MaturedDealSlipModal
            transaction={maturedSlip.transaction}
            dealType={maturedSlip.dealType}
            variant={maturedSlip.variant || 'matured'}
            onClose={() =>
              setMaturedSlip({ open: false, transaction: null, dealType: 'GSec', variant: 'matured' })
            }
          />
        )}

        <Dialog open={slipChoice.open} onClose={() => setSlipChoice({ open: false, deal: null })}>
          <DialogTitle>Select slip type</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary">
              Which slip would you like to open for this deal?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => setSlipChoice({ open: false, deal: null })}>Cancel</Button>
            <Button variant="outlined" onClick={() => openSlip(slipChoice.deal, 'deal')}>
              Deal Slip
            </Button>
            {slipChoice.deal?.event_type !== 'settlement' && (
              <>
                <Button variant="contained" onClick={() => openSlip(slipChoice.deal, 'matured')}>
                  Matured Deal Slip
                </Button>
                <Button variant="contained" color="secondary" onClick={() => openSlip(slipChoice.deal, 'rollover')}>
                  Roll over Deal Slip
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default MaturityHandlingPage;
